import { ControllerManager } from '@gui/controller/ControllerManager';
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormLabel,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    Slider,
    Stack,
    Typography,
} from '@mui/material';
import { LineColor, NotificationType, RaysLoadingStatus, SIM_TARGET, SIM_VVA } from '@visualizer/types';
import React, { useEffect, useMemo } from 'react';
import LayerStandard from '../../../../Simulations/LayerStandard';
import { getGrid, useLoadingStore } from '../../../../store/LoadingStore';
import { LinesColorModeOptions, selectLinesColorMode } from '../../../store/ColorScheme';
import usePersistentStore, { getLayersState, useLayersBucket } from '../../../store/persistent/persistentStore';
import useNotificationStore from '@gui/store/notification';
import LoadableComponent from '../../generic/LoadableComponent';
import DownloadController, { formatBytes } from '../../generic/DownloadController';
import Loading from './Loading';
import useViewerStore from 'store/viewerStore';
import { Download01 } from '@untitledui-pro/icons/line';
import { checkKey } from 'Loader/IndexedDB_Handler';
import { getRaysSizeForLayer } from './raysSizes';
import { useCapabilities } from '../../../../demo/capabilities';

type RaysProps = {
    activeSensorId: number;
    isCurrentSensorValid: boolean;
    loadRays: boolean;
    setLoadRays: (value: boolean) => void;
    onLinesAvailabilityChange?: (hasLines: boolean) => void;
};

type DownloadUiState =
    | 'checking-cache'
    | 'preparing-after-download'
    | 'preparing-download'
    | 'other-layer-downloading'
    | 'downloading'
    | 'idle'
    | 'loaded';

const Rays: React.FC<RaysProps> = ({ activeSensorId, isCurrentSensorValid, loadRays, setLoadRays, onLinesAvailabilityChange }) => {
    const { raysEnabledByDefault } = useCapabilities();
    // Local UI state mirrored from operator
    const [loadingStatus, setLoadingStatus] = React.useState<RaysLoadingStatus>(RaysLoadingStatus.NotLoaded);
    const [loadingErrorMessage, setLoadingErrorMessage] = React.useState('');
    const [confirmAllOpen, setConfirmAllOpen] = React.useState(false);
    const [pendingSpQty, setPendingSpQty] = React.useState<number | null>(null);
    const { simulationItems } = usePersistentStore((s) => s.getActiveLayerState());
    const { segments } = usePersistentStore((s) => s.getActiveLayerState());
    const activeResultId = usePersistentStore((s) => s.getActiveLayerState().data?.activeResultId ?? 0);
    const { linesLength, freeTargetLinesLength, showFreeLines, showIntersectingLines, showRays, raysEveryN, raysQuantity } =
        simulationItems;
    const [raysSpQuantity, setRaysSpQuantity] = React.useState(0);
    const [raysMode, setRaysMode] = React.useState<'visible' | 'all'>('all');
    const [lastUserVisibility, setLastUserVisibility] = React.useState<boolean | null>(null);
    const [isCheckingRaysCache, setIsCheckingRaysCache] = React.useState(true);
    const [isRaysDownloaded, setIsRaysDownloaded] = React.useState(false);
    const [raysAssetSizeBytes, setRaysAssetSizeBytes] = React.useState<number | null>(null);
    const [hasConfirmedNoLinesForLayer, setHasConfirmedNoLinesForLayer] = React.useState(false);
    const [isStartingLinesDownload, setIsStartingLinesDownload] = React.useState(false);
    const [downloadAllLayers, setDownloadAllLayers] = React.useState(false);
    const cacheCheckSeqRef = React.useRef(0);
    const showNotification = useNotificationStore((s) => s.showNotification);

    const updateCurrentLayerState = usePersistentStore((s) => s.updateCurrentLayerState);
    const updateLayerState = usePersistentStore((s) => s.updateLayerState);

    const activeLayerId = useLayersBucket((s) => s.activeLayerID);
    const hasAutoLoadedRays = React.useRef(false);
    const activeLayer = useMemo(() => getGrid().getActiveLayer?.(), [activeLayerId]);
    const downloadQueueMap = useLoadingStore((state) => state.downloadQueue);
    const activeLinesDownloadKey = useLoadingStore((state) => state.activeLinesDownloadKey);
    const isAllLayersLinesDownloadActive = useLoadingStore((state) => state.isAllLayersLinesDownloadActive);
    const setActiveLinesDownloadKey = useLoadingStore((state) => state.setActiveLinesDownloadKey);
    const setAllLayersLinesDownloadActive = useLoadingStore((state) => state.setAllLayersLinesDownloadActive);
    const activeSimKind = activeLayer?.getSimKind?.();
    const isFreeTargetLayer = activeSimKind === SIM_VVA || activeSimKind === SIM_TARGET;
    const lineLengthKey = isFreeTargetLayer ? 'freeTargetLinesLength' : 'linesLength';
    const effectiveLinesLength = isFreeTargetLayer ? freeTargetLinesLength : linesLength;
    const raysCacheKey = activeLayer instanceof LayerStandard ? activeLayer.getRaysCacheKey() : null;
    // Cache the active layer id locally and react to external changes
    const [cachedActiveLayerId, setCachedActiveLayerId] = React.useState<string>(() => getLayersState().activeLayerID);
    const cachedActiveLayerIdRef = React.useRef(cachedActiveLayerId);

    const handleChange = (key: string, value: boolean | number | number[] | LineColor) => {
        updateCurrentLayerState({
            simulationItems: {
                ...simulationItems,
                [key]: value,
            },
        });
    };

    const cleanupLayerRays = React.useCallback(
        (layerId: string | null | undefined, resetLoadState = false) => {
            if (!layerId) return;

            const layer = getGrid().searchLayerByUID(layerId);
            if (layer instanceof LayerStandard) {
                layer.disposeRays();
                layer.unloadRays(setLoadingStatus);
            }

            const storedSimulationItems = usePersistentStore.getState().layers.layerStates[layerId]?.simulationItems;
            if (storedSimulationItems?.showRays) {
                updateLayerState(layerId, {
                    simulationItems: {
                        showRays: false,
                    },
                });
            }

            if (resetLoadState) {
                setLoadRays(false);
            }
        },
        [setLoadRays, updateLayerState]
    );

    //Using ref because unmount cachedActiveLayerId could be already changed
    useEffect(() => {
        cachedActiveLayerIdRef.current = cachedActiveLayerId;
    }, [cachedActiveLayerId]);

    useEffect(() => {
        return () => {
            cleanupLayerRays(cachedActiveLayerIdRef.current, true);
        };
    }, [cleanupLayerRays]);

    const handleChangeRaysSpQuantity = (_: unknown, v: string) => {
        const num = Number(v);
        if (num === 1) {
            // Ask for confirmation before applying "All"
            setPendingSpQty(1);
            setConfirmAllOpen(true);
            return;
        }
        setRaysSpQuantity(num);
    };

    useEffect(() => {
        const activeLayer = getGrid().getActiveLayer();
        if (loadRays) {
            if (activeLayer instanceof LayerStandard) {
                activeLayer.loadItems(setLoadingStatus, 'rays', (message) => {
                    setLoadingErrorMessage(message || 'Failed to load lines. The file may be missing or invalid.');
                });
            }
        } else {
            if (activeLayer instanceof LayerStandard) activeLayer.unloadRays(setLoadingStatus);
            setLoadingErrorMessage('');
            if (showRays) {
                handleChange('showRays', false);
            }
        }
    }, [loadRays]);

    // Set rays to visible by default when loaded:
    useEffect(() => {
        if (loadingStatus !== RaysLoadingStatus.Loaded) return;

        // Only decide visibility if user never expressed intent
        if (lastUserVisibility !== null) {
            handleChange('showRays', lastUserVisibility);
            return;
        }

        // First-time default
        if (!showRays) {
            handleChange('showRays', true);
        }
    }, [loadingStatus]);

    useEffect(() => {
        // Hard reset on layer switch to avoid showing stale controls from previous layer.
        setIsCheckingRaysCache(true);
        setIsRaysDownloaded(false);
        setRaysAssetSizeBytes(null);
        setHasConfirmedNoLinesForLayer(false);
        setIsStartingLinesDownload(false);
        setDownloadAllLayers(false);
        setLoadingErrorMessage('');

        // Reset Visibility Flags:
        setLastUserVisibility(null);

        // Dispose of the previous layer and clear its visibility flags.
        cleanupLayerRays(cachedActiveLayerId, true);
        setCachedActiveLayerId(activeLayerId);
        setRaysSpQuantity(0); // Reset all button
    }, [activeLayerId, cachedActiveLayerId, cleanupLayerRays]);

    const activeSensorNumber = useViewerStore((s) => s.activeSensorPoints);
    const colorModeOption = simulationItems.linesColorModeOption;
    useEffect(() => {
        const pipelineController = ControllerManager.getInstance().getPipelineController();

        const activeLayer: LayerStandard = getGrid().getActiveLayer() as LayerStandard;
        if (!(activeLayer instanceof LayerStandard)) return;

        const lastResult = pipelineController.getLastResult();
        if (showRays && loadRays && lastResult) {
            const activeSensorsIds = lastResult.data.map((sp) => sp.getID());
            const showOnlySelected = raysSpQuantity === 0;
            const selectedSensorId = activeSensorsIds.includes(activeSensorId) ? [activeSensorId] : [];
            activeLayer.updateRaysMesh(showRays, showOnlySelected ? selectedSensorId : activeSensorsIds, raysMode);
        } else {
            activeLayer.disposeRays();
        }
    }, [
        effectiveLinesLength,
        showFreeLines,
        showIntersectingLines,
        loadRays,
        showRays,
        raysEveryN,
        raysQuantity,
        raysSpQuantity,
        raysMode,
        colorModeOption,
        activeSensorId,
        segments?.frameSelected,
        segments?.isAggregate,
        segments?.framesList,
        activeSensorNumber,
    ]);

    const handleColorModeChange = (newValue: LinesColorModeOptions) => {
        updateCurrentLayerState({
            simulationItems: {
                ...simulationItems,
                linesColorModeOption: newValue,
                linesColorMode: selectLinesColorMode(newValue),
            },
        });
    };

    const showFixedQuantitySlider = useMemo(() => raysQuantity === 1, [raysQuantity]);
    const raysDownloadQueue = useMemo(() => {
        if (!raysCacheKey) return [];
        const item = downloadQueueMap[raysCacheKey];
        return item ? [item] : [];
    }, [downloadQueueMap, raysCacheKey]);

    // Render fixed quantity slider
    const renderFixedQuantitySlider = () => {
        if (!showFixedQuantitySlider) return null;

        return (
            <Box sx={{ mt: 1 }}>
                <Typography gutterBottom>Rays quantity</Typography>
                <Slider
                    className="panel-slider"
                    size="small"
                    value={raysEveryN}
                    valueLabelDisplay="on"
                    onChange={(_, v) => handleChange('raysEveryN', v)}
                    step={1}
                    min={1}
                    max={100}
                />
            </Box>
        );
    };

    // Render main controls when rays are loaded
    const renderMainControls = () => {
        return (
            <>
                <Box sx={{ mt: 1, pl: 1, pr: 2 }}>
                    <Typography gutterBottom>Line Length</Typography>
                    <Slider
                        className="panel-slider"
                        size="small"
                        value={effectiveLinesLength}
                        valueLabelDisplay="on"
                        onChange={(_, v) => handleChange(lineLengthKey, v)}
                        step={1}
                        min={1}
                        max={100}
                    />
                </Box>

                <FormControl fullWidth sx={{ mt: 1, px: 0.5, mb: 1 }}>
                    <InputLabel id="color-mode-label">Color Mode</InputLabel>
                    <Select
                        size="small"
                        labelId="color-mode-label"
                        label="Color Mode"
                        value={colorModeOption}
                        onChange={(e) => handleColorModeChange(e.target.value)}
                    >
                        {Object.values(LinesColorModeOptions).map((option) => (
                            <MenuItem key={option} value={option}>
                                {option}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Box sx={{ flex: 1, ml: 0.5, mb: 1 }}>
                    <FormControl component="fieldset">
                        <FormLabel component="legend">Lines color mapping</FormLabel>
                        <RadioGroup
                            className="panel-compact-control-stack"
                            sx={{ gap: 1.2 }}
                            row
                            value={raysMode}
                            onChange={(_, v) => setRaysMode(v as 'visible' | 'all')}
                        >
                            <FormControlLabel value="visible" control={<Radio />} label="Visible" />
                            <FormControlLabel value="all" control={<Radio />} label="All" />
                        </RadioGroup>
                    </FormControl>
                </Box>
                <Stack>
                    <Box sx={{ flex: 1, ml: 0.5, mb: 1 }}>
                        <FormControl component="fieldset">
                            <FormLabel component="legend">Sensor Point</FormLabel>
                            <RadioGroup
                                className="panel-compact-control-stack"
                                sx={{ gap: 1.2 }}
                                row
                                value={raysSpQuantity}
                                onChange={handleChangeRaysSpQuantity}
                            >
                                <FormControlLabel value={0} control={<Radio />} label="Selected" />
                                <FormControlLabel value={1} control={<Radio />} label="All" />
                            </RadioGroup>
                        </FormControl>
                    </Box>

                    {/* RENDER RAYS QUANTITY */}
                    <Box sx={{ flex: 1, ml: 0.5, mb: 1 }}>
                        <FormControl component="fieldset">
                            <FormLabel component="legend">Lines</FormLabel>
                            <RadioGroup
                                className="panel-compact-control-stack"
                                sx={{ gap: 1.2 }}
                                row
                                value={raysQuantity}
                                onChange={(_, v) => handleChange('raysQuantity', Number(v))}
                            >
                                <FormControlLabel disabled value={0} control={<Radio />} label="Single" />
                                <FormControlLabel value={1} control={<Radio />} label="Fixed N" />
                                <FormControlLabel value={2} control={<Radio />} label="All" />
                            </RadioGroup>
                        </FormControl>
                    </Box>
                    {renderFixedQuantitySlider()}
                </Stack>
                <Dialog
                    open={confirmAllOpen}
                    onClose={() => {
                        setConfirmAllOpen(false);
                        setPendingSpQty(null);
                    }}
                >
                    <DialogTitle>Apply to all elements?</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Selecting <strong>All</strong> will compute/show lines for every element. This may be heavy and take longer to
                            load. Do you want to proceed?
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={() => {
                                setConfirmAllOpen(false);
                                setPendingSpQty(null);
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={() => {
                                if (pendingSpQty !== null) {
                                    setRaysSpQuantity(pendingSpQty);
                                }
                                setConfirmAllOpen(false);
                                setPendingSpQty(null);
                            }}
                            autoFocus
                        >
                            Confirm
                        </Button>
                    </DialogActions>
                </Dialog>
            </>
        );
    };

    const checkRaysCache = React.useCallback(async () => {
        const checkSeq = ++cacheCheckSeqRef.current;
        const layerIdAtStart = getLayersState().activeLayerID;
        const layer = getGrid().getActiveLayer();
        if (!(layer instanceof LayerStandard)) {
            if (checkSeq !== cacheCheckSeqRef.current || getLayersState().activeLayerID !== layerIdAtStart) return;
            setIsRaysDownloaded(false);
            setRaysAssetSizeBytes(null);
            setHasConfirmedNoLinesForLayer(false);
            onLinesAvailabilityChange?.(false);
            setIsCheckingRaysCache(false);
            return;
        }

        const key = layer.getRaysCacheKey();
        setIsCheckingRaysCache(true);
        try {
            const cached = await checkKey(key);
            if (checkSeq !== cacheCheckSeqRef.current || getLayersState().activeLayerID !== layerIdAtStart) return;
            setIsRaysDownloaded(cached);
            if (cached) {
                setRaysAssetSizeBytes(null);
                setHasConfirmedNoLinesForLayer(false);
                onLinesAvailabilityChange?.(true);
            } else {
                try {
                    const simulationSetId = layer.getSimulationSetId();
                    const subSimulationId = layer.getSubSimulationId();

                    if (!simulationSetId || subSimulationId == null) {
                        throw new Error('Missing simulation set or sub simulation id for lines size lookup');
                    }

                    const size = await getRaysSizeForLayer(simulationSetId, subSimulationId);
                    if (checkSeq !== cacheCheckSeqRef.current || getLayersState().activeLayerID !== layerIdAtStart) return;
                    setRaysAssetSizeBytes(size);
                    setHasConfirmedNoLinesForLayer(!(size !== null && size > 0));
                    onLinesAvailabilityChange?.(size !== null && size > 0);
                } catch (error) {
                    if (checkSeq !== cacheCheckSeqRef.current || getLayersState().activeLayerID !== layerIdAtStart) return;
                    console.warn('Unable to fetch lines size metadata:', error);
                    setRaysAssetSizeBytes(null);
                    setHasConfirmedNoLinesForLayer(false);
                    onLinesAvailabilityChange?.(true);
                }
            }
        } finally {
            if (checkSeq !== cacheCheckSeqRef.current || getLayersState().activeLayerID !== layerIdAtStart) return;
            setIsCheckingRaysCache(false);
        }
    }, [onLinesAvailabilityChange]);

    const getLayersPendingRaysDownload = React.useCallback(async () => {
        const layers = getGrid()
            .getAllLayers()
            .filter((layer): layer is LayerStandard => layer instanceof LayerStandard);
        const pendingLayers: Array<{
            layer: LayerStandard;
            cacheKey: string;
            endpoint: string;
        }> = [];

        for (const layer of layers) {
            const cacheKey = layer.getRaysCacheKey();
            if (await checkKey(cacheKey)) continue;

            try {
                const size = await getRaysSizeForLayer(layer.getSimulationSetId(), layer.getSubSimulationId());
                if (size === 0) continue;
            } catch (error) {
                console.warn(`Unable to fetch lines size metadata for layer ${layer.name}:`, error);
            }

            pendingLayers.push({
                layer,
                cacheKey,
                endpoint: layer.getRaysEndpoint(),
            });
        }

        return pendingLayers;
    }, []);

    const handleDownloadRays = React.useCallback(
        async ({ allLayers }: { allLayers: boolean }) => {
            const layer = getGrid().getActiveLayer();
            if (!(layer instanceof LayerStandard)) return;
            const currentLayerRaysKey = layer.getRaysCacheKey();
            const existingDownloadItem = useLoadingStore.getState().downloadQueue[currentLayerRaysKey];
            const lockOwner = useLoadingStore.getState().activeLinesDownloadKey;
            const isCurrentLayerDownloadActive =
                existingDownloadItem != null &&
                existingDownloadItem.status !== 'completed' &&
                existingDownloadItem.status !== 'cached' &&
                existingDownloadItem.status !== 'error';

            if (isCurrentLayerDownloadActive || (lockOwner && lockOwner !== currentLayerRaysKey)) {
                return;
            }

            setIsStartingLinesDownload(true);
            if (allLayers) {
                setAllLayersLinesDownloadActive(true);
                setActiveLinesDownloadKey(currentLayerRaysKey);
            }
            try {
                const pendingLayers = allLayers
                    ? await (async () => {
                          const activeLayerId = layer.getId();
                          const layers = await getLayersPendingRaysDownload();
                          const activeIndex = layers.findIndex((entry) => entry.layer.getId() === activeLayerId);
                          if (activeIndex <= 0) return layers;

                          const [activeEntry] = layers.splice(activeIndex, 1);
                          return [activeEntry, ...layers];
                      })()
                    : [
                          {
                              layer,
                              cacheKey: currentLayerRaysKey,
                              endpoint: layer.getRaysEndpoint(),
                          },
                      ];

                if (pendingLayers.length === 0) {
                    if (getLayersState().activeLayerID === layer.getId()) {
                        setIsRaysDownloaded(true);
                        onLinesAvailabilityChange?.(true);
                    }
                    return;
                }

                useLoadingStore.getState().fileFetcher.primeDownloadQueue(
                    pendingLayers.map(({ cacheKey, endpoint, layer }) => ({
                        key: cacheKey,
                        endpoint,
                        label: `${layer.name} lines`,
                    }))
                );

                const failedLayers: string[] = [];

                for (const pendingLayer of pendingLayers) {
                    setActiveLinesDownloadKey(pendingLayer.cacheKey);

                    try {
                        await pendingLayer.layer.downloadRays();
                        if (getLayersState().activeLayerID === pendingLayer.layer.getId()) {
                            setIsRaysDownloaded(true);
                            onLinesAvailabilityChange?.(true);
                        }
                    } catch (error) {
                        console.error(`Failed to download lines for layer ${pendingLayer.layer.name}:`, error);
                        failedLayers.push(pendingLayer.layer.name);
                    } finally {
                        if (useLoadingStore.getState().activeLinesDownloadKey === pendingLayer.cacheKey) {
                            setActiveLinesDownloadKey(null);
                        }
                    }
                }

                if (failedLayers.length > 0) {
                    showNotification(
                        failedLayers.length === 1
                            ? `Failed to download lines for ${failedLayers[0]}.`
                            : `Failed to download lines for ${failedLayers.length} layers.`,
                        NotificationType.Error
                    );
                }
            } catch (error) {
                console.error('Failed to download lines:', error);
                showNotification('Failed to download lines.', NotificationType.Error);
            } finally {
                setIsStartingLinesDownload(false);
                if (useLoadingStore.getState().activeLinesDownloadKey === currentLayerRaysKey) {
                    setActiveLinesDownloadKey(null);
                }
                if (allLayers) {
                    setAllLayersLinesDownloadActive(false);
                }
            }
        },
        [
            getLayersPendingRaysDownload,
            onLinesAvailabilityChange,
            setActiveLinesDownloadKey,
            setAllLayersLinesDownloadActive,
            showNotification,
        ]
    );

    useEffect(() => {
        checkRaysCache().catch((error) => {
            console.error('Failed to check rays cache:', error);
            setIsCheckingRaysCache(false);
        });
    }, [activeLayerId, activeResultId, checkRaysCache]);

    useEffect(() => {
        if (raysEnabledByDefault && !isCheckingRaysCache && !isRaysDownloaded && activeLayerId) {
            void handleDownloadRays({ allLayers: false });
        }
    }, [raysEnabledByDefault, isCheckingRaysCache, isRaysDownloaded, activeLayerId, handleDownloadRays]);

    useEffect(() => {
        hasAutoLoadedRays.current = false;
    }, [activeLayerId]);

    useEffect(() => {
        if (raysEnabledByDefault && isRaysDownloaded && !loadRays && !hasAutoLoadedRays.current) {
            hasAutoLoadedRays.current = true;
            setLoadRays(true);
        }
    }, [raysEnabledByDefault, isRaysDownloaded, loadRays, setLoadRays]);

    const statusForContent = loadingStatus;
    const isLoadingRays = loadingStatus === RaysLoadingStatus.Initializing || loadingStatus === RaysLoadingStatus.Loading;
    const showRaysToggles = loadRays && statusForContent === RaysLoadingStatus.Loaded;
    const shouldHideLinesSection = !isCheckingRaysCache && !isRaysDownloaded && hasConfirmedNoLinesForLayer;
    const showDownloadUI = !isCheckingRaysCache && !isRaysDownloaded && !hasConfirmedNoLinesForLayer;
    const activeDownloadItem = raysDownloadQueue[0];
    const isCurrentLayerDownloadInProgress =
        Boolean(activeDownloadItem) &&
        activeDownloadItem?.status !== 'completed' &&
        activeDownloadItem?.status !== 'cached' &&
        activeDownloadItem?.status !== 'error';
    const isCurrentLayerDownloadQueued = activeDownloadItem?.status === 'queued';
    const showDownloadStartLoader = showDownloadUI && isStartingLinesDownload && !isCurrentLayerDownloadInProgress;
    const isOtherLayerDownloadInProgress = Boolean(activeLinesDownloadKey && raysCacheKey && activeLinesDownloadKey !== raysCacheKey);
    const isTransferCompleted =
        activeDownloadItem?.status === 'completed' || activeDownloadItem?.status === 'cached' || activeDownloadItem?.status === 'error';
    const downloadLabel = raysAssetSizeBytes ? `Download Lines (${formatBytes(raysAssetSizeBytes)})` : 'Download Lines';
    const downloadUiState: DownloadUiState = (() => {
        if (isCheckingRaysCache) return 'checking-cache';
        if (!showDownloadUI) return 'loaded';
        if (isTransferCompleted) return 'preparing-after-download';
        if (
            isCurrentLayerDownloadQueued ||
            (isAllLayersLinesDownloadActive && !isCurrentLayerDownloadInProgress && activeDownloadItem?.status !== 'error')
        ) {
            return 'preparing-download';
        }
        if (isOtherLayerDownloadInProgress && !isAllLayersLinesDownloadActive && !activeDownloadItem) {
            return 'other-layer-downloading';
        }
        if (showDownloadStartLoader) return 'preparing-download';
        if (isCurrentLayerDownloadInProgress && !isCurrentLayerDownloadQueued) return 'downloading';
        return 'idle';
    })();

    if (shouldHideLinesSection) {
        return null;
    }

    const renderDownloadUi = () => {
        switch (downloadUiState) {
            case 'checking-cache':
                return <Typography variant="caption">Checking lines cache...</Typography>;
            case 'preparing-after-download':
                return (
                    <Box sx={{ my: 0.75, display: 'flex', justifyContent: 'center' }}>
                        <LoadableComponent loading={true} indicatorPlacement="end" disableWhileLoading={false}>
                            <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                <Typography variant="caption">Preparing...</Typography>
                            </Box>
                        </LoadableComponent>
                    </Box>
                );
            case 'other-layer-downloading':
                return (
                    <Box sx={{ my: 0.75, display: 'flex', justifyContent: 'center' }}>
                        <LoadableComponent loading={true} indicatorPlacement="end" disableWhileLoading={false}>
                            <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                <Typography variant="caption">Another layer lines download is in progress.</Typography>
                            </Box>
                        </LoadableComponent>
                    </Box>
                );
            case 'preparing-download':
                return (
                    <Box sx={{ my: 0.75, display: 'flex', justifyContent: 'center' }}>
                        <LoadableComponent loading={true} indicatorPlacement="end" disableWhileLoading={false}>
                            <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                <Typography variant="caption">Preparing download...</Typography>
                            </Box>
                        </LoadableComponent>
                    </Box>
                );
            case 'downloading':
                return (
                    <Box sx={{ mt: 0.75, px: 0.75 }}>
                        <DownloadController
                            queue={raysDownloadQueue}
                            progressMode="bytes"
                            showPrimarySummary={false}
                            secondaryAlign="center"
                            compact
                            showReadyMetric={false}
                            showDataTotal
                        />
                    </Box>
                );
            case 'idle':
                return (
                    <Box sx={{ ml: 0.4, mt: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
                            <Box
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 0.75,
                                    cursor: isOtherLayerDownloadInProgress || isCurrentLayerDownloadInProgress ? 'not-allowed' : 'pointer',
                                    color: 'var(--loading-summary-text)',
                                    opacity: isOtherLayerDownloadInProgress || isCurrentLayerDownloadInProgress ? 0.55 : 1,
                                }}
                                onClick={() => {
                                    if (isOtherLayerDownloadInProgress || isCurrentLayerDownloadInProgress) return;
                                    void handleDownloadRays({ allLayers: downloadAllLayers });
                                }}
                                role="button"
                                aria-label="Download Lines"
                            >
                                <Download01 className="icon-size-medium" />
                                <Typography variant="body2">{downloadLabel}</Typography>
                            </Box>
                            <Box
                                component="label"
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 0.2,
                                    ml: 0.15,
                                    color: 'var(--loading-summary-text)',
                                    cursor: isOtherLayerDownloadInProgress || isCurrentLayerDownloadInProgress ? 'not-allowed' : 'pointer',
                                    opacity: isOtherLayerDownloadInProgress || isCurrentLayerDownloadInProgress ? 0.55 : 1,
                                }}
                            >
                                <Checkbox
                                    size="small"
                                    checked={downloadAllLayers}
                                    onChange={(e) => setDownloadAllLayers(e.target.checked)}
                                    disabled={isOtherLayerDownloadInProgress || isCurrentLayerDownloadInProgress}
                                    sx={{
                                        padding: 0,
                                        '& .MuiSvgIcon-root': {
                                            fontSize: '0.78rem',
                                        },
                                    }}
                                />
                                <Typography
                                    variant="body2"
                                    sx={{
                                        userSelect: 'none',
                                    }}
                                >
                                    For all layers
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                );
            case 'loaded':
                return (
                    <LoadableComponent loading={isLoadingRays} indicatorPlacement="end" disableWhileLoading={false}>
                        <Stack spacing={0}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    flexWrap: 'nowrap',
                                    ml: 0,
                                    my: 0.5,
                                    columnGap: 0.6,
                                    rowGap: 0,
                                    '& .MuiFormControlLabel-root': { margin: 0, minHeight: 26, padding: 0 },
                                    '& .MuiCheckbox-root': { padding: 0.25 },
                                    '& .MuiFormControlLabel-label': { lineHeight: 1.1, whiteSpace: 'nowrap' },
                                }}
                            >
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            size="small"
                                            name="loadRays"
                                            checked={loadRays}
                                            onChange={(e) => setLoadRays(e.target.checked)}
                                        />
                                    }
                                    label="Lines"
                                />
                            </Box>

                            {showRaysToggles && (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        flexWrap: 'wrap',
                                        mt: 0.25,
                                        columnGap: 0.8,
                                        rowGap: 0.25,
                                        '& .MuiFormControlLabel-root': { margin: 0, minHeight: 26, padding: 0 },
                                        '& .MuiCheckbox-root': { padding: 0.25 },
                                        '& .MuiFormControlLabel-label': { lineHeight: 1.1, whiteSpace: 'nowrap' },
                                    }}
                                >
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                size="small"
                                                checked={showRays}
                                                onChange={(e) => {
                                                    const value = e.target.checked;
                                                    setLastUserVisibility(value);
                                                    handleChange('showRays', value);
                                                }}
                                            />
                                        }
                                        label="Visible"
                                    />

                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                size="small"
                                                checked={showFreeLines}
                                                onChange={(e) => handleChange('showFreeLines', e.target.checked)}
                                            />
                                        }
                                        label="Free"
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                size="small"
                                                checked={showIntersectingLines}
                                                onChange={(e) => handleChange('showIntersectingLines', e.target.checked)}
                                            />
                                        }
                                        label="Intersected"
                                    />
                                </Box>
                            )}
                        </Stack>
                    </LoadableComponent>
                );
        }
    };

    return (
        <Box>
            {renderDownloadUi()}
            {!showDownloadUI && !isCheckingRaysCache && isRaysDownloaded && (
                <Box sx={loadingStatus === RaysLoadingStatus.Initializing ? { ml: 0.4 } : undefined}>
                    <Loading status={statusForContent} errorMessage={loadingErrorMessage}>
                        {isCurrentSensorValid ? renderMainControls() : 'This sensor is not valid. Move around'}
                    </Loading>
                </Box>
            )}
        </Box>
    );
};

export default Rays;
